/**
 * Task Detail Modal Component
 * 
 * Full-page modal for viewing and editing task details.
 * Includes title, description, properties, comments, and attachments.
 * Similar to Focalboard's card detail view.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Button,
  Form,
  Badge,
  ListGroup,
  Spinner,
  Alert,
  InputGroup,
} from 'react-bootstrap';
import { TaskCard, ProjectBoard, Comment } from '../../types';
import {
  updateTask,
  deleteTask,
  addComment,
  getComments,
} from '../../services/projectService';
import { TeamMember, getTeamMembers } from '../../services/teamService';
import './TaskDetailModal.css';

interface TaskDetailModalProps {
  show: boolean;
  task: TaskCard;
  board: ProjectBoard;
  userEmail: string;
  adminId: string; // Admin document ID for subcollection paths
  onClose: () => void;
  onUpdate: (task: TaskCard) => void;
  onDelete: (taskId: string) => void;
}

/**
 * Task Detail Modal for comprehensive task viewing and editing
 */
const TaskDetailModal: React.FC<TaskDetailModalProps> = ({
  show,
  task,
  board,
  userEmail,
  adminId,
  onClose,
  onUpdate,
  onDelete,
}) => {
  const [editedTask, setEditedTask] = useState<TaskCard>(task);
  const [comments, setComments] = useState<Comment[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [newComment, setNewComment] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);

  /**
   * Reset edited task when task prop changes
   */
  useEffect(() => {
    setEditedTask(task);
    setHasUnsavedChanges(false);
  }, [task]);


  /**
   * Load team members for assignee dropdown
   */
  const loadTeamMembers = useCallback(async () => {
    try {
      const members = await getTeamMembers(adminId);
      setTeamMembers(members);
    } catch (err) {
      console.error('Error loading team members:', err);
    }
  }, [adminId]);

  /**
   * Load comments for the task
   */
  const loadComments = useCallback(async () => {
    setIsLoading(true);
    try {
      const fetchedComments = await getComments(adminId, task.boardId, task.id);
      setComments(fetchedComments);
    } catch (err) {
      console.error('Error loading comments:', err);
      setError('Failed to load comments');
    } finally {
      setIsLoading(false);
    }
  }, [adminId, task.boardId, task.id]);

  /**
   * Load comments and team members when modal opens
   */
  useEffect(() => {
    if (show) {
      loadComments();
      loadTeamMembers();
    }
  }, [show, loadComments, loadTeamMembers]);

  /**
   * Handle task field update
   */
  const handleFieldChange = (field: keyof TaskCard, value: any) => {
    setEditedTask(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  /**
   * Save task changes
   */
  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      await updateTask(adminId, task.boardId, task.id, editedTask);
      onUpdate(editedTask);
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error('Error saving task:', err);
      setError('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle task deletion
   */
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await deleteTask(adminId, task.boardId, task.id);
      onDelete(task.id);
    } catch (err) {
      console.error('Error deleting task:', err);
      setError('Failed to delete task');
      setIsSaving(false);
    }
  };

  /**
   * Add a new comment
   */
  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      const comment = await addComment(
        adminId,
        task.boardId,
        task.id,
        newComment,
        userEmail,
        userEmail.split('@')[0], // Simple username extraction
        userEmail
      );

      setComments(prev => [...prev, comment]);
      setNewComment('');

      // Update comment count
      const updatedTask = { ...editedTask, commentCount: (editedTask.commentCount || 0) + 1 };
      setEditedTask(updatedTask);
      onUpdate(updatedTask);
    } catch (err) {
      console.error('Error adding comment:', err);
      setError('Failed to add comment');
    }
  };

  /**
   * Get status options from board properties
   */
  const getStatusOptions = () => {
    const statusProperty = board.properties.find(p => p.id === 'status');
    return statusProperty?.options || [];
  };

  /**
   * Get priority options
   */
  const getPriorityOptions = () => {
    const priorityProperty = board.properties.find(p => p.id === 'priority');
    return priorityProperty?.options || [];
  };

  /**
   * Format date for input
   */
  const formatDateForInput = (date: Date | null | undefined): string => {
    if (!date) return '';
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  /**
   * Parse date from input
   */
  const parseDateFromInput = (dateString: string): Date | null => {
    if (!dateString) return null;
    return new Date(dateString);
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onClose}
        size="lg"
        className="task-detail-modal"
        backdrop={hasUnsavedChanges ? 'static' : true}
        keyboard={!hasUnsavedChanges}
      >
        <Modal.Header closeButton className="task-detail-header">
          <div className="task-detail-header-content">
            <div className="task-header-icon">
              <i className="bi bi-card-checklist"></i>
            </div>
            <div className="task-header-title">
              <label className="task-header-label">Task Title</label>
              <Form.Control
                type="text"
                value={editedTask.title}
                onChange={(e) => handleFieldChange('title', e.target.value)}
                className="task-title-input"
                placeholder="Enter task title"
              />
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="task-detail-body">
          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          )}

          {/* Task Properties Section */}
          <div className="task-section">
            <h5 className="section-title">Properties</h5>

            <div className="task-properties">
              {/* Status */}
              <div className="property-row">
                <label className="property-label">
                  <i className="bi bi-kanban me-2"></i>
                  Status
                </label>
                <Form.Select
                  value={editedTask.status}
                  onChange={(e) => handleFieldChange('status', e.target.value)}
                  size="sm"
                >
                  {getStatusOptions().map(option => (
                    <option key={option.id} value={option.id}>
                      {option.value}
                    </option>
                  ))}
                </Form.Select>
              </div>

              {/* Priority */}
              <div className="property-row">
                <label className="property-label">
                  <i className="bi bi-flag me-2"></i>
                  Priority
                </label>
                <Form.Select
                  value={editedTask.priority || ''}
                  onChange={(e) => handleFieldChange('priority', e.target.value || null)}
                  size="sm"
                >
                  <option value="">None</option>
                  {getPriorityOptions().map(option => (
                    <option key={option.id} value={option.id}>
                      {option.value}
                    </option>
                  ))}
                </Form.Select>
              </div>

              {/* Assignee */}
              <div className="property-row">
                <label className="property-label">
                  <i className="bi bi-person me-2"></i>
                  Assignee
                </label>
                <Form.Select
                  value={editedTask.assignee || ''}
                  onChange={(e) => handleFieldChange('assignee', e.target.value || null)}
                  size="sm"
                >
                  <option value="">Unassigned</option>
                  {teamMembers.map((member) => (
                    <option key={member.id} value={member.email}>
                      {member.name} ({member.tag}) - {member.email}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  {teamMembers.length === 0 && 'No team members yet. Create members in Admin Settings.'}
                </Form.Text>
              </div>

              {/* Due Date */}
              <div className="property-row">
                <label className="property-label">
                  <i className="bi bi-calendar-event me-2"></i>
                  Due Date
                </label>
                <Form.Control
                  type="date"
                  value={formatDateForInput(editedTask.dueDate)}
                  onChange={(e) => handleFieldChange('dueDate', e.target.value ? parseDateFromInput(e.target.value) : null)}
                  size="sm"
                />
              </div>
            </div>
          </div>

          {/* Description Section */}
          <div className="task-section">
            <h5 className="section-title">
              <i className="bi bi-text-left me-2"></i>
              Description
            </h5>
            <Form.Control
              as="textarea"
              rows={6}
              value={editedTask.description || ''}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              placeholder="Add a more detailed description..."
              className="task-description-textarea"
            />
          </div>

          {/* Task Image Section */}
          {(task.imageUrl || editedTask.imageUrl) && (
            <div className="task-section">
              <h5 className="section-title">
                <i className="bi bi-image me-2"></i>
                Related Image
              </h5>
              <div className="task-detail-image">
                <img
                  src={task.imageUrl || editedTask.imageUrl}
                  alt="Task related"
                  onClick={() => setShowImageModal(true)}
                  style={{
                    width: '100%',
                    maxHeight: '250px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    border: '1px solid #dee2e6'
                  }}
                  onError={(e) => {
                    console.error('Failed to load image:', task.imageUrl || editedTask.imageUrl);
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <small className="text-muted d-block mt-2">
                  <i className="bi bi-zoom-in me-1"></i>
                  Click to view full size
                </small>
              </div>
            </div>
          )}

          {/* Tags Section */}
          <div className="task-section">
            <h5 className="section-title">
              <i className="bi bi-tags me-2"></i>
              Tags
            </h5>
            <Form.Control
              type="text"
              value={editedTask.tags?.join(', ') || ''}
              onChange={(e) => handleFieldChange('tags', e.target.value.split(',').map(t => t.trim()).filter(Boolean))}
              placeholder="Enter tags separated by commas (e.g., urgent, bug-fix, feature)"
              size="sm"
            />
            <Form.Text className="text-muted">
              Separate multiple tags with commas. Press Enter or type comma to add.
            </Form.Text>
            <div className="tags-preview mt-3">
              {editedTask.tags && editedTask.tags.length > 0 ? (
                editedTask.tags.map((tag, index) => (
                  <Badge key={index} bg="secondary" className="tag-badge me-2 mb-2">
                    {tag}
                    <button
                      type="button"
                      className="tag-remove-btn"
                      onClick={() => {
                        const newTags = editedTask.tags?.filter((_, i) => i !== index) || [];
                        handleFieldChange('tags', newTags);
                      }}
                      title="Remove tag"
                    >
                      ×
                    </button>
                  </Badge>
                ))
              ) : (
                <div className="text-muted" style={{ fontSize: '13px' }}>No tags yet</div>
              )}
            </div>
          </div>

          {/* Comments Section */}
          <div className="task-section">
            <h5 className="section-title">
              <i className="bi bi-chat-left-text me-2"></i>
              Comments ({comments.length})
            </h5>

            {/* New Comment Input */}
            <div className="new-comment-section">
              <InputGroup>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      handleAddComment();
                    }
                  }}
                />
              </InputGroup>
              <Button
                variant="primary"
                size="sm"
                onClick={handleAddComment}
                disabled={!newComment.trim()}
                className="mt-2"
              >
                <i className="bi bi-send me-1"></i>
                Add Comment
              </Button>
            </div>

            {/* Comments List */}
            {isLoading ? (
              <div className="text-center my-4">
                <Spinner animation="border" size="sm" />
              </div>
            ) : (
              <ListGroup variant="flush" className="comments-list mt-3">
                {comments.map(comment => (
                  <ListGroup.Item key={comment.id} className="comment-item">
                    <div className="comment-header">
                      <strong>{comment.userName}</strong>
                      <span className="comment-date">
                        {comment.createdAt.toLocaleString()}
                      </span>
                    </div>
                    <div className="comment-text">{comment.text}</div>
                  </ListGroup.Item>
                ))}
                {comments.length === 0 && (
                  <div className="text-center text-muted py-3">
                    No comments yet
                  </div>
                )}
              </ListGroup>
            )}
          </div>

          {/* Metadata Footer */}
          <div className="task-metadata-footer">
            <small className="text-muted">
              Created by {task.createdBy} on {task.createdAt.toLocaleString()}
              {task.updatedAt && task.updatedAt !== task.createdAt && (
                <> • Updated {task.updatedAt.toLocaleString()}</>
              )}
            </small>
          </div>
        </Modal.Body>

        <Modal.Footer className="task-detail-footer">
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={isSaving}
            className="me-auto"
          >
            <i className="bi bi-trash me-1"></i>
            Delete Task
          </Button>

          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>

          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!hasUnsavedChanges || isSaving}
          >
            {isSaving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              <>
                <i className="bi bi-check-lg me-1"></i>
                Save Changes
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Image Modal for full-size view */}
      <Modal
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Task Image - Full Size</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {(task.imageUrl || editedTask.imageUrl) && (
            <img
              src={task.imageUrl || editedTask.imageUrl}
              alt="Task related - full size"
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain'
              }}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImageModal(false)}>
            Close
          </Button>
          {(task.imageUrl || editedTask.imageUrl) && (
            <Button
              variant="primary"
              onClick={() => window.open(task.imageUrl || editedTask.imageUrl, '_blank')}
            >
              <i className="bi bi-arrow-up-right-square me-2"></i>
              Open in New Tab
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TaskDetailModal;

